import { Countdown } from 'components/countdown';
import Button from 'components/ui/button';
import { useAuth } from 'context/AuthContext';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  getTaskCycles,
  getPlayerInfo,
  getTasks,
  getTasksManager,
  getVacationManager,
  getTotalTasks,
} from 'services/dashboard.service';
import { GetTaskLevelLabel, GetTaskStatusLabel } from 'utils/strings';

import ImgDestaque from 'assets/img/destaque-ilustracao.png';
import { User, VacationManagerInterface } from 'types/user';
import VacationManagerModal from 'components/vacation-manager-modal';
import { useModal } from 'context/ModalContext';
import { gravarLateralidade } from 'utils/lateralidade';
import { removeStorageItem } from 'utils/storage';
import { AiOutlineWarning } from 'react-icons/ai';
import Tippy from '@tippyjs/react';
import { BiInfoCircle } from 'react-icons/bi';
import { findLateralMenagers } from 'services/profile.service';
import ModalEquipeLateral from 'pages/MinhaEquipe/components/ModalEquipeLateral';
import { UserNeedingApprovalModal } from 'components/user-needing-approval-modal';
import { TasksPendingApprovalParticipantsModal } from 'components/tasks-pending-approval-participants-modal';
import { TASKS_PENDING_APPROVAL_MODAL_ID } from 'components/tasks-pending-approval-participants-modal/read-notifications';
import { LuBell } from 'react-icons/lu';
import { getMonthNameFromString, getTaskStep } from 'utils/helpers';

import { usePendingAppreciation } from '../usePendingAppreciation';
import { usePendingParticipantApprovals } from '../usePendingParticipantApprovals';
import { canApproveParticipants, canRegisterRealizacao, isLadderManager } from '../home-reads';
import { useServerNow } from '../server-clock';
import {
  agendaCaption,
  agendaDeadline,
  DeadlineNote,
  DraftWarning,
  PhaseAgenda,
  phaseStep,
  PhaseStep,
  ProgramItemRow,
  ProgramSection,
  registrationMeta,
} from './program-section';

interface Props {
  taskManager: any[];
  hasPartialTask: boolean | undefined;
}

// Chamada de pendência do gestor. Com a contagem do aggregate mostra o volume — era
// justamente ele que a home escondia, exibindo "não há nada" a quem tinha centenas.
// Sem contagem (aggregate indisponível), mantém o texto genérico anterior.
function getPendingAppreciationLabel(count: number | null) {
  if (count === null || count <= 0) return 'Você tem Realizações Destaque esperando serem apreciadas!';
  if (count === 1) return 'Você tem 1 Realização Destaque esperando ser apreciada!';

  return `Você tem ${count} Realizações Destaque esperando serem apreciadas!`;
}

export function TaskCycles({ taskManager, hasPartialTask }: Props) {
  const navigate = useNavigate();
  const { user, userInfo, cyclesExtended, cycleSelected } = useAuth();
  const { showModal, closeModal } = useModal();
  const [canRegister, setCanRegister] = useState(true);
  const [predefinedAgreementType, setPredefinedAgreementType] = useState<boolean | undefined>();

  // const [tasks, setTasks] = useState<any>();
  const [currentTaskCycle, setCurrentTaskCycle] = useState<any>();
  const [loadingTasks, setLoadingTasks] = useState(true);
  const [taskCycles, setTaskCycles] = useState<any>();
  const [myTasks, setMyTasks] = useState<any[]>();
  const [totalTask, setTotalTask] = useState<any>();
  const [participationTasks, setParticipationTasks] = useState<any[]>();
  const [vacationManagers, setVacationManagers] = useState<VacationManagerInterface[]>();
  const [evaluateTasks, setEvaluateTasks] = useState<any[]>();
  const [playerInfo, setPlayerInfo] = useState<any>();
  const [cycleActive, setCycleActive] = useState<string>();
  const [evaluateCycleActive, setEvaluateCycleActive] = useState<string>();
  const [lateralManagers, setLateralManagers] = useState<User[]>();
  const [showModalLateral, setShowModalLateral] = useState(false);
  const [isExperied, setIsExperied] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [loadingLateralManagers, setLoadingLateralManagers] = useState(false);

  const tasksPendingApproval = usePendingParticipantApprovals(userInfo);

  // Relógio único da Home (DCE-35, DCE-36); 0 enquanto a hora do servidor não chegou.
  const realDatetime = useServerNow();
  const hasServerTime = realDatetime > 0;
  const userId = user?._id;

  // O ciclo selecionado precisa ser o corrente: o aggregate de elegibilidade resolve
  // sempre o ciclo `open`, então num ciclo passado a contagem não descreveria a tela.
  const selectedCycleIsCurrent = useMemo(
    () => cyclesExtended?.find(x => x._id === cycleSelected?._id)?.status === 'open',
    [cycleSelected, cyclesExtended],
  );

  // Pendências pelo aggregate canônico `apreciacao_flow_eligibility` — a MESMA fonte que
  // o gate de /minha-equipe/validar-apreciar-realizacao usa, e que já valida a janela de
  // cada realização por nível (Validação/N1/N2/Pit Stop/N3), as duplas MAD e a
  // lateralidade. O cache de sessão é COMPARTILHADO com o gate, então home → apreciar é
  // uma requisição só; o hook revalida sozinho quando a aba volta ao foco.
  const pendingApreciacao = usePendingAppreciation({
    playerId: user?._id,
    userInfo,
    currentCycle: selectedCycleIsCurrent,
  });

  const activeCycleObj = useMemo(() => taskCycles?.find((item: any) => item._id === cycleActive), [cycleActive, taskCycles]);

  const evaluateActiveCycleObj = useMemo(
    () => taskCycles?.find((item: any) => item._id === evaluateCycleActive),
    [evaluateCycleActive, taskCycles],
  );

  const taskPerCycle: any = useMemo(() => {
    if (!Array.isArray(myTasks) || !taskCycles?.length || !cycleActive) return null;

    const cycle = taskCycles.find((cycle: any) => cycle._id === cycleActive);

    let _task = myTasks;

    if (
      (!myTasks || !myTasks?.length || myTasks?.length < 1) &&
      participationTasks &&
      participationTasks?.length &&
      participationTasks?.length > 0
    ) {
      _task = participationTasks;
    }

    const tasks = _task.filter(
      item => item.created >= cycle.date_start && item.created <= cycle.date_finish && item.status !== 'arquivado',
    );

    return tasks?.length ? tasks[0] : null;
  }, [cycleActive, myTasks, participationTasks, taskCycles]);

  const canViewTabEquipe = useMemo(() => canApproveParticipants(playerInfo), [playerInfo]);

  // const isCurrentEvaluator = useCallback(
  //   (item: any) => {
  //     if (!user || !item || !playerInfo) return false;

  //     if (item.evaluator?.length) {
  //       const matriculaEvaluator = parseInt(
  //         item.evaluator[item.evaluator.length - 1].replace('F', '')
  //       );
  //       const currentId = parseInt(user.extra.id);

  //       return matriculaEvaluator === currentId;
  //     } else {
  //       return playerInfo.cod_etapa && playerInfo.cod_etapa > 0;
  //     }
  //   },
  //   [playerInfo, user]
  // );

  const evaluateTaskPerCycle: any = useMemo(() => {
    if (!Array.isArray(evaluateTasks) || !taskCycles?.length || !evaluateCycleActive) return null;

    const cycle = taskCycles.find((cycle: any) => cycle._id === evaluateCycleActive);

    const tasks = evaluateTasks.filter(item => item.created >= cycle.date_start && item.created <= cycle.date_finish);

    return tasks?.length ? tasks[tasks.length - 1] : null;

    // let tasksFiltered = tasks.filter((item: any) => !!isCurrentEvaluator(item));

    // if (tasksFiltered?.length) {
    //   tasksFiltered = tasksFiltered[tasksFiltered.length - 1];
    // } else {
    //   tasksFiltered = tasks[tasks.length - 1];
    // }

    // console.log(tasksFiltered);

    // return !!tasksFiltered ? tasksFiltered : null;
  }, [evaluateCycleActive, evaluateTasks, taskCycles]);

  const getActiveEvaluationStep: (0 | 2 | 3 | 4 | 4.1 | 5)[] | null = useMemo((): (0 | 2 | 3 | 4 | 4.1 | 5)[] | null => {
    if (!evaluateActiveCycleObj || !realDatetime) return null;

    if (
      realDatetime > evaluateActiveCycleObj.date_evaluation_step2_start &&
      realDatetime < evaluateActiveCycleObj.date_evaluation_step2_finish
    ) {
      // Etapa 2
      return [2];
    } else if (
      realDatetime > evaluateActiveCycleObj.date_evaluation_step3_start &&
      realDatetime < evaluateActiveCycleObj.date_evaluation_step4_finish
    ) {
      // Etapa 3
      return [4];
    } else if (
      realDatetime > evaluateActiveCycleObj.date_evaluation_step4_start &&
      realDatetime < evaluateActiveCycleObj.date_evaluation_step4_finish
    ) {
      // Etapa 4
      return [4];
    } else if (
      realDatetime > evaluateActiveCycleObj.date_evaluation_pitStop_start &&
      realDatetime < evaluateActiveCycleObj.date_evaluation_pitStop_finish
    ) {
      // Etapa 4.1
      return [4.1];
    } else if (
      realDatetime > evaluateActiveCycleObj.date_evaluation_step5_start &&
      realDatetime < evaluateActiveCycleObj.date_evaluation_step5_finish
    ) {
      // Etapa 5
      return [5];
    } else {
      return [0];
    }
  }, [evaluateActiveCycleObj, realDatetime]);

  const getPlayerStep = (task: any) => {
    if (!!task.prosseguir_idGestor && task.prosseguir_idGestor === playerInfo._id) {
      return 4.1;
    }

    if (typeof playerInfo.cod_etapa === 'number') {
      if (playerInfo.cod_etapa === 2) {
        return 2;
      }

      if (playerInfo.cod_etapa === 4) {
        return 4;
      }

      if (playerInfo.cod_etapa === 5) {
        return 5;
      }

      if (playerInfo.cod_etapa === 1 || playerInfo.cod_etapa === 3) {
        if (String(task.original_manager) === String(playerInfo._id)) {
          if (task.status === 'rejeitado' || task.status === 'aguardando') {
            return 2;
          } else {
            return 3;
          }
        } else {
          return 3;
        }
      }

      return 0;
    }

    return 0;
  };

  const htmlCountdownEvaluation = useMemo(() => {
    if (
      !evaluateActiveCycleObj ||
      (!getActiveEvaluationStep && getActiveEvaluationStep !== 0) ||
      !playerInfo ||
      !Array.isArray(evaluateTasks) ||
      !taskCycles?.length ||
      !evaluateCycleActive
    ) {
      setIsExperied(true);
      return <></>;
    }

    const codEtapa =
      playerInfo.cod_etapa === 2
        ? 2
        : playerInfo.cod_etapa === 1 || playerInfo.cod_etapa === 3
          ? 3
          : playerInfo.cod_etapa === 4
            ? 4
            : playerInfo.cod_etapa === 5
              ? 5
              : null;

    const now = +new Date();
    const months = taskCycles.filter((item: { type: string }) => item.type === 'month');
    const monthStage =
      months.find((item: { status: string }) => item.status === 'evaluation') ||
      months.find((item: { status: string }) => item.status === 'open');
    const nivel2 = taskCycles?.filter((item: { type: string }) => item.type === 'trimester');
    const nivel3 = taskCycles?.filter((item: { type: string }) => item.type === 'semester');
    const nivel2OuNivel3Stage =
      nivel2
        ?.sort((a: any, b: any) => a.date_evaluation_step4_start - b.date_evaluation_step4_start)
        ?.find((item: any) => {
          if (item.status === 'evaluation') {
            return true;
          }

          return (
            (item.date_evaluation_step4_start && now < item.date_evaluation_step4_start) ||
            (item.date_evaluation_pitStop_start && now < item.date_evaluation_pitStop_start)
          );
        }) ||
      nivel3
        ?.sort((a: any, b: any) => a.date_evaluation_step5_start - b.date_evaluation_step5_start)
        ?.find((item: any) => {
          if (item.status === 'evaluation') {
            return true;
          }

          return item.date_evaluation_step5_start && now < item.date_evaluation_step5_start;
        }) ||
      months.find((item: { status: string }) => item.status === 'evaluation');

    if (nivel2OuNivel3Stage?.type !== 'month' && nivel2OuNivel3Stage?.status === 'closed') {
      nivel2OuNivel3Stage.status = 'open';
    }

    const _evaluateActiveCycleObj = codEtapa === 4 || codEtapa === 5 ? nivel2OuNivel3Stage : monthStage;

    const cycle = taskCycles.find((cycle: any) => cycle._id === evaluateCycleActive);

    const tasks: any[] = evaluateTasks.filter(task => task.created >= cycle.date_start && task.created <= cycle.date_finish);
    if (_evaluateActiveCycleObj?.status === 'open') {
      setIsExperied(false);
      return (
        <span>
          O período de avaliação desta fase inicia em:{' '}
          <strong className="font-semibold whitespace-nowrap">
            <Countdown urgentClassName="text-urgent"
              hideIcon
              key="missao"
              deadline={
                new Date(
                  codEtapa === 4
                    ? _evaluateActiveCycleObj.date_evaluation_step4_start
                    : codEtapa === 5
                      ? _evaluateActiveCycleObj.date_evaluation_step5_start
                      : _evaluateActiveCycleObj.date_finish,
                )
              }
            />
          </strong>
        </span>
      );
    } else if (_evaluateActiveCycleObj?.status === 'evaluation') {
      if (tasks.length) {
        const userTaskStep: number[] = tasks
          .map(task => (typeof getPlayerStep(task) === 'number' ? (getPlayerStep(task) as number) : 0))
          .sort(() => -1);

        const isStepIncluded = getActiveEvaluationStep
          ? getActiveEvaluationStep.some(step => userTaskStep.includes(step))
          : false;

        if (isStepIncluded) {
          const date_finish = getActiveEvaluationStep.includes(2)
            ? _evaluateActiveCycleObj?.date_evaluation_step2_finish
            : getActiveEvaluationStep.includes(3)
              ? _evaluateActiveCycleObj?.date_evaluation_step3_finish
              : getActiveEvaluationStep.includes(4)
                ? _evaluateActiveCycleObj?.date_evaluation_step4_finish
                : getActiveEvaluationStep.includes(4.1)
                  ? _evaluateActiveCycleObj?.date_evaluation_pitStop_finish
                  : _evaluateActiveCycleObj?.date_evaluation_step5_finish;

          setIsExperied(false);

          return (
            <span>
              O período de apreciação deste nível termina em:{' '}
              <strong className="font-semibold whitespace-nowrap">
                <Countdown urgentClassName="text-urgent" hideIcon key="missao" deadline={new Date(date_finish)} />
              </strong>
            </span>
          );
        } else {
          const hasAfter = getActiveEvaluationStep
            ? userTaskStep?.find(x => getActiveEvaluationStep.some(step => x > step))
            : undefined;

          const date_start =
            hasAfter === 2
              ? _evaluateActiveCycleObj?.date_evaluation_step2_start
              : hasAfter === 3
                ? _evaluateActiveCycleObj?.date_evaluation_step3_start
                : hasAfter === 4
                  ? _evaluateActiveCycleObj?.date_evaluation_step4_start
                  : hasAfter === 4.1
                    ? _evaluateActiveCycleObj?.date_evaluation_pitStop_start
                    : _evaluateActiveCycleObj?.date_evaluation_step5_start;

          if (!!hasAfter) {
            setIsExperied(false);
            setIsStarting(true);
            return (
              <span>
                O período de apreciação começa em:{' '}
                <strong className="font-semibold whitespace-nowrap">
                  <Countdown urgentClassName="text-urgent" hideIcon key="missao" deadline={new Date(date_start)} />
                </strong>
              </span>
            );
          } else {
            setIsExperied(true);
            return <></>;
          }
        }
      } else {
        const codEtapa =
          playerInfo.cod_etapa === 2
            ? 2
            : playerInfo.cod_etapa === 1 || playerInfo.cod_etapa === 3
              ? 3
              : playerInfo.cod_etapa === 4
                ? 4
                : playerInfo.cod_etapa === 5
                  ? 5
                  : null;

        if (codEtapa) {
          if (getActiveEvaluationStep.includes(codEtapa)) {
            const date_finish = getActiveEvaluationStep.includes(2)
              ? _evaluateActiveCycleObj?.date_evaluation_step2_finish
              : getActiveEvaluationStep.includes(3)
                ? _evaluateActiveCycleObj?.date_evaluation_step3_finish
                : getActiveEvaluationStep.includes(4)
                  ? _evaluateActiveCycleObj?.date_evaluation_step4_finish
                  : _evaluateActiveCycleObj?.date_evaluation_step5_finish;

            setIsExperied(false);
            return (
              <span>
                O período de apreciação deste nível termina em:{' '}
                <strong className="font-semibold whitespace-nowrap">
                  <Countdown urgentClassName="text-urgent" hideIcon key="missao" deadline={new Date(date_finish)} />
                </strong>
              </span>
            );
          } else {
            if (getActiveEvaluationStep?.some(step => step < codEtapa)) {
              const date_start =
                codEtapa === 2
                  ? _evaluateActiveCycleObj?.date_evaluation_step2_start
                  : codEtapa === 3
                    ? _evaluateActiveCycleObj?.date_evaluation_step3_start
                    : codEtapa === 4
                      ? _evaluateActiveCycleObj?.date_evaluation_step4_start
                      : _evaluateActiveCycleObj?.date_evaluation_step5_start;
              setIsExperied(false);
              setIsStarting(true);
              return (
                <span>
                  O período de apreciação começa em:{' '}
                  <strong className="font-semibold whitespace-nowrap">
                    <Countdown urgentClassName="text-urgent" hideIcon key="missao" deadline={new Date(date_start)} />
                  </strong>
                </span>
              );
            } else {
              setIsExperied(true);
              return <></>;
            }
          }
        } else {
          setIsExperied(true);
          return <></>;
        }
      }
    } else {
      setIsExperied(true);
      return <></>;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evaluateActiveCycleObj, evaluateCycleActive, evaluateTasks, getActiveEvaluationStep, playerInfo, taskCycles]);

  // Fallback local, usado só enquanto o aggregate não respondeu (ou falhou).
  //
  // Atenção ao tipo: a home é alimentada pelo `tasks_manager_qtd`, que projeta `evaluator`
  // e `original_manager` CRUS — arrays/strings de matrícula, sem os `*_detail` que o
  // `tasks_manager` hidrata. Comparar `lastManager._id` aqui lia `undefined` em toda
  // realização e derrubava a lista inteira, que é como o gestor com centenas de pendências
  // acabava vendo "não há nenhuma". A dupla é um par não-ordenado, então checar se o
  // responsável está na MINHA dupla é equivalente ao antigo caminho inverso.
  const hasToEvaluateLocal = useMemo(() => {
    if (!Array.isArray(evaluateTasks)) {
      return false;
    }

    const tasks = evaluateTasks.filter((item: any) => {
      const lastManager = item?.evaluator?.length ? item.evaluator[item.evaluator.length - 1] : item.original_manager;
      const taskStep = getTaskStep(item);

      if (
        lastManager &&
        String(lastManager) !== String(playerInfo?._id) &&
        (!Array.isArray(playerInfo?.mad_dupla) ||
          !playerInfo.mad_dupla.some((md: any) => String(md._id) === String(lastManager)))
      ) {
        return false;
      }

      // if (getActiveEvaluationStep?.includes(2) && lastManager === playerInfo?._id && item.status !== 'aguardando') {
      //   return false;
      // }

      if (
        item.status === 'concluidoAuto' ||
        item.status === 'concluido' ||
        item.status === 'selo' ||
        item.status === 'valeu' ||
        item.status === 'mandou' ||
        item.status === 'rejeitado'
      ) {
        return false;
      }

      // if (
      //   getActiveEvaluationStep?.some(step => step > 2) && // Verifica se algum valor é maior que 2
      //   lastManager === playerInfo?._id &&
      //   (item.status === 'concluidoAuto' || item.status === 'concluido' || item.status === 'selo')
      // ) {
      //   return false;
      // }

      if (taskStep && !getActiveEvaluationStep?.includes(taskStep)) {
        return false;
      }

      return true;
    });

    return tasks.length > 0;
  }, [evaluateTasks, getActiveEvaluationStep, playerInfo]);

  // O aggregate manda quando disponível. O filtro local só enxerga a fase VISÍVEL
  // (`getActiveEvaluationStep`, derivada de um único estágio) e o recorte que o
  // `tasks_manager_qtd` trouxe; o aggregate percorre o ciclo inteiro, nível a nível.
  const hasToEvaluate = useMemo(
    () => (pendingApreciacao === null ? hasToEvaluateLocal : pendingApreciacao > 0),
    [hasToEvaluateLocal, pendingApreciacao],
  );

  const showHasEvaluated = useMemo(() => {
    // Com a contagem, "não há nada aguardando" é exatamente zero pendências. Não cruzamos
    // com `isExperied`/`isStarting`: ambos derivam da fase visível e apagavam a caixa de
    // quem tinha pendência num nível diferente do corrente.
    if (pendingApreciacao !== null) {
      return pendingApreciacao === 0;
    }

    return !isExperied && !hasToEvaluate && !isStarting;
  }, [hasToEvaluate, isExperied, isStarting, pendingApreciacao]);

  const showPendingAppreciation = useMemo(() => {
    if (pendingApreciacao !== null) {
      return pendingApreciacao > 0;
    }

    return !isExperied && hasToEvaluate && !isStarting;
  }, [hasToEvaluate, isExperied, isStarting, pendingApreciacao]);

  // Etapa do mês (fase) que a agenda descreve: a em apreciação, senão a aberta.
  const agendaStage = useMemo(() => {
    if (!Array.isArray(taskCycles)) return undefined;

    const stages = taskCycles.filter(item => item.type === 'month');
    return stages.find(item => item.status === 'evaluation') || stages.find(item => item.status === 'open');
  }, [taskCycles]);

  // Etapas da "Agenda da fase" (HOME-21). As datas de cada nível são as de sempre; o Pit Stop
  // é uma etapa própria, com a janela dele.
  const agendaSteps: PhaseStep[] = useMemo(() => {
    if (!playerInfo || !Array.isArray(taskCycles) || !taskCycles.length || !agendaStage) return [];

    const stages = taskCycles.filter(item => item.type === 'month');
    const nivel2OuNivel3Stages = taskCycles.filter(item => item.type === 'semester' || item.type === 'trimester');
    const openNivel2OuNivel3 = nivel2OuNivel3Stages.filter(item => item.status === 'open');
    const nivel2OuNivel3 =
      nivel2OuNivel3Stages.find(item => item.status === 'evaluation') ||
      stages.find(item => item.status === 'evaluation') ||
      (openNivel2OuNivel3.length ? openNivel2OuNivel3[openNivel2OuNivel3.length - 1] : nivel2OuNivel3Stages[0]);

    return [
      phaseStep('Validação', agendaStage.date_evaluation_step2_start, agendaStage.date_evaluation_step2_finish),
      phaseStep('Nível 1', agendaStage.date_evaluation_step3_start, agendaStage.date_evaluation_step3_finish),
      phaseStep('Nível 2', nivel2OuNivel3?.date_evaluation_step4_start, nivel2OuNivel3?.date_evaluation_step4_finish),
      phaseStep('Pit Stop', nivel2OuNivel3?.date_evaluation_pitStop_start, nivel2OuNivel3?.date_evaluation_pitStop_finish),
      phaseStep('Nível 3', nivel2OuNivel3?.date_evaluation_step5_start, nivel2OuNivel3?.date_evaluation_step5_finish),
    ];
  }, [agendaStage, playerInfo, taskCycles]);

  function renderAgenda() {
    return <PhaseAgenda steps={agendaSteps} caption={agendaCaption(agendaStage?.title, cycleSelected?.title)} />;
  }

  useEffect(() => {
    async function getUserInfo() {
      try {
        const _playerInfo: any = userInfo;

        if (_playerInfo && Array.isArray(cyclesExtended) && cyclesExtended.length && !!cycleSelected) {
          setPlayerInfo(_playerInfo);

          const playerCadMissao = canRegisterRealizacao(_playerInfo);
          // const playerTipoDeAcordo =
          //   !_playerInfo.tipo_de_acordo ||
          //   (typeof _playerInfo?.tipo_de_acordo === 'string' &&
          //     !!_playerInfo?.tipo_de_acordo &&
          //     !!_playerInfo?.tipo_de_acordo?.match(/HIBRIDO/gi));

          // setPredefinedAgreementType(
          //   typeof _playerInfo.cad_missao &&
          //     _playerInfo.cad_missao > 0 &&
          //     typeof _playerInfo?.tipo_de_acordo === 'string' &&
          //     !!_playerInfo?.tipo_de_acordo &&
          //     !!_playerInfo?.tipo_de_acordo?.match(/PREDEFINIDO/gi),
          // );
          setPredefinedAgreementType(false);
          // setCanRegister(playerCadMissao && playerTipoDeAcordo);
          setCanRegister(playerCadMissao);

          const cycleActive = cyclesExtended.find(x => x._id === cycleSelected?._id);
          const _currentCycle = !!cycleActive ? cycleActive._id : cyclesExtended[0]._id;
          const _stages = cyclesExtended?.find(x => x._id === _currentCycle)?.steps;
          setTaskCycles(_stages);

          if (_stages) {
            const currentCycle = _stages.find((item: any) => item.status === 'open' && item.type && item.type === 'month');
            setCycleActive(currentCycle?._id || _stages.find((item: any) => item.type && item.type === 'month')?._id);
          }

          // Pode cadastrar
          if (playerCadMissao && _stages) {
            const data = await getTasks(user?._id);

            const my_tasks = data.filter((item: any) => item.owner === user?._id);
            const other_tasks = data.filter((item: any) => item.owner !== user?._id);

            setMyTasks(my_tasks);
            setParticipationTasks(other_tasks);

            // const _totalTask = await getTotalTasks();
            // setTotalTask(_totalTask);
          }
        }

        setLoadingTasks(false);
      } catch (e) {
        console.log(e);
      }
    }

    if (
      user &&
      !playerInfo &&
      Array.isArray(taskManager) &&
      hasServerTime &&
      !taskCycles &&
      Array.isArray(cyclesExtended) &&
      cyclesExtended.length &&
      !!cycleSelected
    ) {
      getUserInfo();
    }
    // Só primitivos de player e hora: nova referência de `user`/`userInfo` (refreshUser) ou o
    // tique de 1 s do relógio não reavaliam a leitura (DCE-28).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canViewTabEquipe, playerInfo, taskManager, userId, userInfo?.chave, hasServerTime, taskCycles, cyclesExtended, cycleSelected]);

  useEffect(() => {
    async function getUserInfo() {
      const _playerInfo: any = userInfo;

      // Gestor
      if (isLadderManager(_playerInfo)) {
        const currentCycle = taskCycles.find((item: any) => item.status === 'evaluation');
        const cycleId = currentCycle?._id || taskCycles.find((item: any) => item.type && item.type === 'month')?._id;

        const evaluateStage = taskCycles?.find((item: any) => item._id === cycleId);

        if (evaluateStage) {
          const data = taskManager?.filter(
            (e: any) =>
              evaluateStage.date_start &&
              evaluateStage.date_finish &&
              e.created >= evaluateStage.date_start &&
              e.created <= evaluateStage.date_finish,
          );

          setEvaluateTasks(data);
        }
        setEvaluateCycleActive(cycleId);

        if (!loadingLateralManagers && !Array.isArray(lateralManagers)) {
          setLoadingLateralManagers(true);
          const managers = await findLateralMenagers();
          setLateralManagers(managers || []);
          setLoadingLateralManagers(false);
        }
      }
    }

    if (Array.isArray(taskManager) && taskCycles && userInfo) {
      getUserInfo();
    }
  }, [getActiveEvaluationStep, lateralManagers, loadingLateralManagers, taskCycles, taskManager, userInfo]);

  useEffect(() => {
    removeStorageItem('manager_lateralidade');
  }, []);

  function getDescription() {
    return (
      <p>
        Você possui acordo individual do tipo pré-definido na Plataforma Conexão e por isso, não realiza o cadastramento, mas pode
        participar de ações de outras equipes, participando da premiação coletiva. <br />
        <br />
        Para premiação individual a pontuação final das Realizações Destaque será pelo Placar Consolidado da Plataforma Conexão.
      </p>
    );
  }

  const canRegisterNow = user?.extra?.task !== false && activeCycleObj?.status === 'open' && !playerInfo?.visualizador;
  const phaseCountdown =
    activeCycleObj?.status === 'open' ? (
      <DeadlineNote>
        A fase será finalizada em:{' '}
        <strong className="font-semibold">
          <Countdown urgentClassName="text-urgent" hideIcon key="missao" deadline={new Date(activeCycleObj?.date_finish)} />
        </strong>
      </DeadlineNote>
    ) : undefined;

  // Prazo do participante pela agenda da fase (HOME-25).
  const agendaNote = agendaDeadline(agendaSteps);
  const participantNote = agendaNote ? <DeadlineNote>{agendaNote}</DeadlineNote> : undefined;

  const registerButton = (
    <div className="relative inline-flex">
      <Button
        variant="solido-destaque"
        size="sm"
        onClick={() => navigate('/realizacao-destaque/cadastrar')}
        aria-label="Cadastrar realização. Abre modal"
      >
        Cadastrar realização
      </Button>
      {!!hasPartialTask && (
        <DraftWarning
          message="Você possui uma realização em preenchimento. Clique no botão para continuar."
          onClick={() => navigate('/realizacao-destaque/cadastrar')}
        />
      )}
    </div>
  );

  const followButton = (
    <Button
      variant="texto"
      size="sm"
      onClick={() => navigate('/realizacao-destaque')}
      aria-label="Acompanhar realizações destaque"
    >
      Acompanhar realizações
    </Button>
  );

  // Ações do gestor que se repetem com e sem pendência.
  const managerExtraActions = (
    <>
      {!!lateralManagers?.length && (
        <Button variant="texto" size="sm" onClick={() => setShowModalLateral(true)}>
          Apreciar realizações lateralidade/substituição
        </Button>
      )}

      {userInfo?.tipo_de_acordo === 'HIBRIDO' && (
        <Button variant="texto" size="sm" onClick={() => navigate('/realizacao-destaque')}>
          Acompanhar minhas realizações
        </Button>
      )}

      {Array.isArray(tasksPendingApproval) && !!tasksPendingApproval.length && (
        <button
          type="button"
          onClick={() => showModal(TASKS_PENDING_APPROVAL_MODAL_ID)}
          aria-label={`Participações aguardando sua aprovação: ${tasksPendingApproval.length}`}
          className="relative flex h-8 w-8 items-center justify-center rounded-full bg-brand-yellow-light text-base text-brand-blue focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-blue-dark"
        >
          <LuBell aria-hidden="true" />
          <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-brand-pink px-1 text-center text-xs text-brand-white">
            {tasksPendingApproval.length}
          </span>
        </button>
      )}
    </>
  );

  // D3: 150px não tem degrau no Tailwind.
  const illustration = <img src={ImgDestaque} alt="" className="block w-[150px]" />;

  // Chamada do participante com a fase aberta, com ou sem realização na fase (HOME-28).
  const openPhaseHeadline = 'Que tal cadastrar uma Realização Destaque nesta fase?';
  const openPhaseDescription = (
    <p>
      Cadastre sua Realização Destaque, seja reconhecido pela sua liderança e ganhe recompensas!
      <br />
      <b>Lembre-se:</b> você só pode cadastrar uma realização por fase e ela deve estar concluída.
    </p>
  );
  const isPhaseOpen = activeCycleObj?.status === 'open';

  return (
    <>
      {loadingTasks && (
        <ProgramSection program="destaque" name="Realizações Destaque">
          <p role="status" className="text-sm text-content-soft">
            Carregando informações...
          </p>
        </ProgramSection>
      )}

      {/* Cadastro */}
      {!loadingTasks &&
        !!canRegister &&
        (!!taskPerCycle ? (
          <ProgramSection
            program="destaque"
            name="Realizações Destaque"
            headline={isPhaseOpen ? openPhaseHeadline : undefined}
            description={
              isPhaseOpen ? openPhaseDescription : <p>Visualize suas Realizações Destaque e acompanhe os status de apreciação.</p>
            }
            illustration={illustration}
            actions={
              <>
                {(!myTasks || !myTasks?.length || myTasks?.length < 1) && canRegisterNow && registerButton}
                {followButton}
              </>
            }
            note={participantNote}
          >
            {renderAgenda()}

            <ProgramItemRow
              eyebrow="Última Realização Destaque"
              title={
                <>
                  <span className="screen-reader-text">Nome da entrega: </span>
                  {taskPerCycle.title}
                </>
              }
              meta={registrationMeta({
                relation: taskPerCycle.owner === user?._id ? 'Sua realização' : 'Você participa',
                detail: taskPerCycle.stageNumber >= 1 ? `Nível ${taskPerCycle.stageNumber}` : undefined,
                created: taskPerCycle.created,
              })}
              status={GetTaskLevelLabel(taskPerCycle.status)}
              statusLabel="Status da entrega"
              to="/realizacao-destaque"
            />
          </ProgramSection>
        ) : (
          <ProgramSection
            program="destaque"
            name="Realizações Destaque"
            headline={isPhaseOpen ? openPhaseHeadline : 'Você não possui Realização Destaque cadastrada nesta fase.'}
            description={
              isPhaseOpen ? openPhaseDescription : <p>Visualize suas Realizações Destaque e acompanhe os status de apreciação.</p>
            }
            illustration={illustration}
            actions={
              <>
                {canRegisterNow && registerButton}
                {followButton}
              </>
            }
            note={participantNote}
          >
            {renderAgenda()}
          </ProgramSection>
        ))}

      {!loadingTasks && !canRegister && !!predefinedAgreementType && (
        <ProgramSection
          program="destaque"
          name="Realizações Destaque"
          headline={
            <span className="inline-flex items-center gap-2">
              <AiOutlineWarning aria-hidden="true" size={22} className="shrink-0" />
              Você possui acordo individual do tipo pré-definido na Plataforma Conexão.
              <Tippy
                arrow={true}
                allowHTML={true}
                className="tippy rounded-xl bg-black p-2 font-thin text-white"
                content={getDescription()}
              >
                <button type="button" aria-label="Saiba mais sobre o acordo pré-definido" className="inline-flex">
                  <BiInfoCircle aria-hidden="true" size={18} />
                </button>
              </Tippy>
            </span>
          }
          description={<p>Visualize suas Realizações Destaque e acompanhe os status de apreciação.</p>}
          illustration={illustration}
          actions={
            <Button variant="texto" size="sm" onClick={() => navigate('/realizacao-destaque')}>
              Acompanhar Realizações Destaque
            </Button>
          }
          note={phaseCountdown}
        >
          {renderAgenda()}
        </ProgramSection>
      )}

      {/* Gestor */}
      {!loadingTasks &&
        !!playerInfo?.is_manager &&
        playerInfo?.cod_etapa <= 5 &&
        // `hasToEvaluate` entra na condição porque a pendência pode existir sem que o
        // `tasks_manager_qtd` tenha trazido a realização — é o caso do parceiro de dupla
        // que só consta como `non_evaluator`. Sem isso ele cairia no ramo vazio, sem o
        // botão de apreciar.
        (!!evaluateTaskPerCycle || hasToEvaluate ? (
          <ProgramSection
            program="destaque"
            name="Realizações Destaque"
            headline={
              showPendingAppreciation
                ? getPendingAppreciationLabel(pendingApreciacao)
                : showHasEvaluated
                  ? 'Não há nenhuma Realização Destaque aguardando apreciação'
                  : undefined
            }
            description={
              <>
                {!showHasEvaluated && <p>Aprecie as realizações da sua equipe e visualize as realizações que participa.</p>}
                {showHasEvaluated ? (
                  <p>Acesse o botão "Acompanhar Realizações" para visualizar as apreciações das fases e ciclos anteriores.</p>
                ) : (
                  <p className="mt-1">
                    Acesse as Realizações Destaque da sua equipe e inicie a etapa de apreciação. <b>Lembre-se:</b> você
                    precisa apreciar as realizações da sua equipe antes do prazo expirar.
                  </p>
                )}
              </>
            }
            illustration={illustration}
            actions={
              <>
                <Button variant="solido-destaque" size="sm" onClick={() => navigate('/minha-equipe/validar-apreciar-realizacao')}>
                  Apreciar realizações
                </Button>
                {managerExtraActions}
              </>
            }
            note={!isExperied ? <DeadlineNote>{htmlCountdownEvaluation}</DeadlineNote> : undefined}
          >
            {renderAgenda()}
          </ProgramSection>
        ) : (
          <ProgramSection
            program="destaque"
            name="Realizações Destaque"
            headline="Não há nenhuma Realização Destaque aguardando apreciação"
            description={
              <p>Acesse o botão "Acompanhar Realizações" para visualizar as apreciações das fases e ciclos anteriores.</p>
            }
            illustration={illustration}
            actions={
              <>
                <Button variant="solido-destaque" size="sm" onClick={() => navigate('/minha-equipe/validar-apreciar-realizacao')}>
                  Acompanhar realizações
                </Button>
                {managerExtraActions}
              </>
            }
            note={!isExperied ? <DeadlineNote>{htmlCountdownEvaluation}</DeadlineNote> : undefined}
          >
            {renderAgenda()}
          </ProgramSection>
        ))}

      {/* MEQ2-09: o mesmo seletor da Minha Equipe, no lugar do modal antigo; a fila lê a matrícula escolhida. */}
      {!!lateralManagers?.length && (
        <ModalEquipeLateral
          isOpen={showModalLateral}
          titulo="Apreciar realizações lateralidade/substituição"
          texto="Escolha o gestor do seu prefixo no lugar de quem você vai apreciar as realizações destaque."
          rotuloDoConfirmar="Apreciar"
          onFechar={() => setShowModalLateral(false)}
          onEscolher={gestor => {
            gravarLateralidade(gestor.chave);
            setShowModalLateral(false);
            navigate('/minha-equipe/validar-apreciar-realizacao');
          }}
        />
      )}

      <TasksPendingApprovalParticipantsModal tasks={tasksPendingApproval} />
    </>
  );
}
